<template>
  <v-form
    ref="form"
    v-model="payload"
    @submit.prevent="save()"
  >
    <page-template
      :card="false"
    >
      <template slot="title">
        <page-title
          :title="pageTitle"
          :navigate-back-route="backRoute"
        />
      </template>
      <template slot="content">
        <v-container
          class="py-0"
          fluid
          grid-list-sm
        >
          <div>
            <v-layout
              wrap
              justify-start
            >
              <v-flex xm12>
                <v-stepper v-model="stepper">
                  <v-stepper-header>
                    <v-stepper-step
                      v-for="(step, idx) in steps"
                      :key="`step_header_${step.value}_${idx}`"
                      :complete="stepper > step.value"
                      :step="step.value"
                      editable
                    >
                      {{ step.text }}
                    </v-stepper-step>
                  </v-stepper-header>
                  <v-stepper-items>
                    <v-stepper-content
                      v-for="(step, idx) in steps"
                      :key="`step_content_${step.value}_${idx}`"
                      :step="step.value"
                    >
                      <v-layout
                        row
                        wrap
                      >
                        <v-flex
                          xs12
                        >
                          <component
                            :is="step.component"
                            :ref="step.ref"
                            @handle-tab-message="navigateToTabMessage($event)"
                          >
                            <v-card-actions>
                              <v-spacer />
                              <v-btn
                                outlined
                                @click="backNavigate()"
                              >
                                Cancelar
                              </v-btn>
                              <v-btn
                                v-if="idx < (steps.length - 1)"
                                color="primary"
                                class="ml-2"
                                @click="stepper = step.value + 1"
                              >
                                Próximo
                              </v-btn>
                              <communication-send-modal
                                v-else-if="isSend"
                              />
                              <v-btn
                                v-else
                                color="success"
                                class="ml-2"
                                type="submit"
                                :loading="loading"
                              >
                                Salvar
                              </v-btn>
                            </v-card-actions>
                          </component>
                        </v-flex>
                      </v-layout>
                    </v-stepper-content>
                  </v-stepper-items>
                </v-stepper>
              </v-flex>
            </v-layout>
          </div>
        </v-container>
      </template>
    </page-template>
  </v-form>
</template>

<script>
import formMixin from 'Support/components/crud/form-mixin';
import PageTemplate from 'Support/layout/PageTemplate.vue';
import PageTitle from 'Support/layout/PageTitle.vue';
import CommunicationRequest from 'Domain/communication/CommunicationRequest';
import FormGroup from 'Support/layout/FormGroup.vue';
import routerMixin from 'Support/mixins/router-mixin';
import SmsCommunicationRequest from 'Domain/communication/SmsCommunicationRequest';
import EmailCommunicationRequest from 'Domain/communication/EmailCommunicationRequest';
import PushCommunicationRequest from 'Domain/communication/PushCommunicationRequest';
import CommunicationSendModal from './components/CommunicationSendModal.vue';
import CommunicationManagerContent from './components/CommunicationManagerContent.vue';
import CommunicationManagerClientContentVue from './components/CommunicationManagerClientContent.vue';
import CommunicationSetupNotification from './components/CommunicationSetupNotification.vue';
import communicationData from './communicationData';
import CommunicationReviewContentVue from './components/CommunicationReviewContent.vue';

export default {
  name: 'CommunicationForm',
  components: {
    PageTemplate,
    PageTitle,
    FormGroup,
    CommunicationSendModal,
    CommunicationManagerContent,
    CommunicationManagerClientContentVue,
    CommunicationSetupNotification,
    CommunicationReviewContentVue,
  },
  mixins: [routerMixin, formMixin],
  data: () => ({
    requestClass: CommunicationRequest,
    messagesRequest: {
      sms: SmsCommunicationRequest,
      email: EmailCommunicationRequest,
      push: PushCommunicationRequest,
    },
    stepper: 1,
    steps: [
      {
        text: 'Configuração',
        component: 'CommunicationManagerContent',
        value: 1,
      },
      {
        text: 'Público alvo',
        component: 'CommunicationManagerClientContentVue',
        value: 2,
      },
      {
        text: 'Notificações',
        component: 'CommunicationSetupNotification',
        ref: 'messages',
        value: 3,
      },
      {
        text: 'Revisão',
        component: 'CommunicationReviewContentVue',
        value: 4,
      },
    ],
  }),
  computed: {
    pageTitle() {
      if (this.isEdit) {
        return 'Visualizar notificação';
      }
      return 'Criar notificação';
    },
    backRoute() {
      return 'communication';
    },
    isEdit() {
      return Number(this.entityId) > 0;
    },
    isSend() {
      return ['ENVIADA', 'AGENDADA'].includes(
        this.payload.status,
      );
    },
    payload() {
      return this.store.payload;
    },
    refMessage() {
      const { messages = [{}] } = this.$refs;
      return messages[0];
    },
    store() {
      return communicationData;
    },
    getSuccessMessage() {
      if (this.isEdit) {
        return 'Notificações atualizada';
      }
      return 'Notificações criadas';
    },
  },
  created() {
    if (!this.isEdit) {
      this.store.load();
    }
  },
  methods: {
    backNavigate() {
      this.navigateTo(
        this.backRoute,
      );
    },
    navigateToTabMessage(tab) {
      this.stepper = 3;
      this.$nextTick(
        () => {
          this.refMessage.handleTab(tab);
        },
      );
    },
    async loadEntityForEdit() {
      try {
        this.loadingForm = true;
        const {
          data: {
            push, email, mensagemSMS: sms, ...payload
          },
        } = await this.requestClass.get(
          this.entityId,
        );
        const { sms: faultSms, push: faultPush, email: faultEmail } = this.store;
        this.store.setData({
          payload,
          sms: sms || faultSms,
          push: push || faultPush,
          email: email || faultEmail,
        });
      } catch (err) {
        this.showMessageError(err);
      } finally {
        this.loadingForm = false;
      }
    },
    async create() {
      await this.handleRequest(
        payload => this.requestClass.save(
          payload,
        ),
      );
    },
    async update() {
      await this.handleRequest(
        payload => this.requestClass.edit(
          this.entityId,
          payload,
        ),
      );
    },
    async handleRequest(request) {
      try {
        this.loadingForm = true;
        const { payload, send, ...messages } = this.store.getDataPayload;
        const { data: { id } } = await request(payload);
        await this.saveMessages(id || this.entityId, messages);
        await this.sendMessage(id, send);
        this.showSuccessToast({
          text: this.getSuccessMessage,
        });
      } catch (err) {
        this.showMessageError(
          err.response ? err.response : {
            data: err,
          },
        );
      } finally {
        this.loadingForm = false;
      }
    },
    async saveMessages(id, messages) {
      await Promise.all(
        Object.keys(messages)
          .reduce(
            (arr, key) => {
              const body = messages[key];
              arr.push(
                this.messagesRequest[key][body.id ? 'edit' : 'save'](
                  body.id || id,
                  body,
                ),
              );

              return arr;
            },
            [],
          ),
      );
    },
    async sendMessage(id, body) {
      if (body) {
        await this.requestClass.send(id, body);
      }
    },
  },
  provide() {
    return {
      communicationData: this.store,
      submitForm: this.save,
    };
  },
};
</script>
